import {
  throwNotInstance,
  throwNotInstanceOrObject,
  throwNotObjectKey,
  throwNotType
} from '@esm-test/guards';
import { SortByEnum } from '../parser/models/testCollection.js';
import { simpleReplace } from '../parser/utils/testCaseUtils.js';
import { TestParserFlags } from './testParserFlags.js';

/**
 * A function to look up context for a given test group.
 * @param groupName - The name of the test group.
 * @returns The context object for the group, or undefined if not found.
 */
export type ContextLookUp = (groupName: string) => any | undefined;

const defaultOptions = {
  builtInFunctions: [
    'beforeAll',
    'beforeEach',
    'afterAll',
    'afterEach'
  ],
  classContextPropertyName: 'suiteContext',
  testContextLookup: (groupName: string) => { },
  testCaseTitleTemplate: simpleReplace,
  parserFlags: new TestParserFlags({}),
  sort: SortByEnum.none
}

/**
 * Configuration options for the test parser.
 */
export class TestParserOptions {
  /**
   * Names of functions to be treated as built-in hooks (e.g., 'beforeEach').
   */
  builtInFunctions: string[] = [];

  /**
   * The name of the property on a class instance that contains the suite context.
   */
  classContextPropertyName?: string;

  /**
   * A function to look up context for a given test group.
   */
  testContextLookup: ContextLookUp = (groupName) => { };

  /**
   * A template function to generate titles for parameterized test cases.
   */
  testCaseTitleTemplate: (title: string, testCases: any[], caseIndex: number) => string = simpleReplace;

  /**
   * Flags to enable or disable specific parser behaviors.
   */
  parserFlags?: TestParserFlags;

  /**
   * The sorting method to apply to the extracted tests.
   */
  sort: SortByEnum = SortByEnum.none;

  /**
   * Initializes a new TestParserOptions instance.
   * @param options - Partial configuration to override defaults.
   */
  constructor(options: Partial<TestParserOptions>) {
    throwNotInstanceOrObject("options", options, TestParserOptions);

    Object.assign(this, defaultOptions, options);

    throwNotInstance("builtInFunctions", this.builtInFunctions, Array);
    throwNotType(
      "classContextPropertyName",
      this.classContextPropertyName,
      "string"
    );
    throwNotInstance("testContextLookup", this.testContextLookup, Function);
    throwNotInstance("testCaseTitleTemplate", this.testCaseTitleTemplate, Function);
    throwNotInstance("parserFlags", this.parserFlags, TestParserFlags);
    throwNotObjectKey("sort", SortByEnum, this.sort);
  }

}
